---
title: Support for Amp and Pi
description: Pi is now built into the Entire CLI, Amp is available as a plugin, and teams can set their own redaction rules. Commit pages show sessions more clearly, and git-sync 0.5.0 improves mirroring.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here. The carbon-based developers have been productive this week and I must reluctantly admit the results are rather useful.

This week we brought more agents to the Entire CLI. Pi moved into the [`Entire CLI`](https://github.com/entireio/cli) as a built-in integration and Amp joined through the [`external-agent`](https://github.com/entireio/external-agents) system. Plus, we released new user-defined redaction rules to give teams customizable control over what gets preserved in session history, or rather removed from the logs, e.g. secrets.

On [Entire Web](https://entire.io/), commit detail pages now make the path from a commit to its sessions much clearer. [git-sync](https://github.com/entireio/git-sync/releases/tag/v0.5.0) also shipped a practical `0.5.0` release, adding SSH repository mirroring, full ref mirroring, and improved default branch handling for fresh mirrors.

Meanwhile, [`go-git`](https://github.com/go-git/go-git) and [`go-billy`](https://github.com/go-git/go-billy) picked up filesystem hardening, parser validation, archive support, and reliability fixes.

Let's break it down in Dispatch 0x000E.

## Entire CLI

_Released via [v0.6.2](https://github.com/entireio/cli/releases/tag/v0.6.2). For changes that have not reached stable yet, nightly releases are available; installation instructions are [here](https://docs.entire.io/cli/installation)._

### Command Improvements

- `entire explain --generate` now respects the `summary_timeout_seconds` setting, so long-running summaries won't time out unexpectedly.
  - Thank you, [@numman-ali](https://github.com/numman-ali) for your contribution!

### Agent Integrations

- Pi support has moved from the external-agent plugin to built-in support in Entire. Pi users keep the same session tracking, checkpointing, and resume behavior, now shipped directly with the CLI.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.51612903225806%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/89f76783a7be608b7c7c646d8dd9ea1f/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F89f76783a7be608b7c7c646d8dd9ea1f%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Pi enable demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- Added [Amp as an external agent](https://github.com/entireio/external-agents/pull/25), bringing [Amp](https://ampcode.com/) into Entire's external-agent ecosystem with hook installation, transcript export, token calculation, modified-file tracking, session handling, and resume support.
  - Thank you, [@savekirk](https://github.com/savekirk) for your contribution!

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.51612903225806%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/9e2ba88e9958aed9fbe4f3f652d541d1/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F9e2ba88e9958aed9fbe4f3f652d541d1%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Amp external agent demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- Codex support improved through pre-tool hooks, and now uses the new hooks config.

### Security and Redaction

- Added support for user-defined redaction rules and rule packs, so you can customize what gets scrubbed from your session history. Read more [here](https://docs.entire.io/security#user-defined-redaction).

### Usability and Reliability

- `entire clean --all` is now faster and shows progress while running.
- Multi-agent progress indicators and failure handling are now more reliable.
- Drill-in scrolling and post-run access in the TUI are smoother.
- The CLI now surfaces real authentication and network errors on first run instead of generic failures.
- Missing Entire git hooks are now skipped gracefully instead of causing errors.
- Fixed cleanup generation metadata materialization.

### Documentation and Contribution

- Added a [first-time contributors guide](https://github.com/entireio/cli/blob/main/docs/first-time-contributors.md) to help new developers get started.

## Entire Web

### Navigation and Display

- Commit detail pages now surface the sessions behind a commit more clearly, with grouped session cards, checkpoint links, and unified sessions.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.74820143884892%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/7876fbde88617ff6814eef0856ab0618/iframe?loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F7876fbde88617ff6814eef0856ab0618%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Commit detail sessions demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- Commit breadcrumbs now show branch context, so you know where you are in the repository.
- List hover animations are now more responsive.
- Commit author avatars are now preserved correctly during sync.

### Repository Overview

- Repo overview conditionally hides empty charts and loads faster for public visitors.

### Recap and Background Processing

- Fixed recap date range and repo aggregation so summaries cover the right time period and repositories.

## git-sync

_Released via [v0.5.0](https://github.com/entireio/git-sync/releases/tag/v0.5.0)._

### SSH Remote Mirroring Support

- `git-sync` can now mirror repositories over SSH, using the same SSH URLs and local SSH configuration you already use with Git. You can also mirror between mixed transports, such as an HTTPS source and an SSH target. `--progress` and `--stats` currently exclude SSH byte counts.

### Full Ref Mirroring

- Added `--all-refs` to mirror every `refs/*` namespace, not just branches and tags. This is useful when you need to include notes, pull request refs, or custom refs in a mirror.
- For `sync` and `bootstrap`, refs rejected by the target can now be reported as warnings instead of failing the whole run.

### Default Branch Handling

- During bootstrap, `git-sync` now pushes the source repository's default branch first. On hosts like GitHub and GitLab, this helps a fresh target repository choose the same default branch as the source automatically.

## go-git

### go-billy Filesystem Updates

Versions [v6.0.0-alpha-1](https://github.com/go-git/go-billy/releases/tag/v6.0.0-alpha.1) and [v5.9.0](https://github.com/go-git/go-billy/releases/tag/v5.9.0) were recently released, with the changes below:

- `go-billy` v5.9.0 and v6.0.0-alpha.1 add safer filesystem handling based on Go's traversal-resistant `os.Root` primitive.
- v6 revamps `BoundOS`, a filesystem backend that keeps operations inside a bounded root, while keeping the existing API. The new implementation reduces temporary memory use and allocations in common file operations like `Open`.
- `ChrootOS` is deprecated in v5 and removed in the v6 release line.
- The releases include security and reliability fixes addressing [CVE-2026-44973](https://github.com/go-git/go-billy/security/advisories/GHSA-qw64-3x98-g7q2) and [CVE-2026-44740](https://github.com/go-git/go-billy/security/advisories/GHSA-m3xc-h892-ggx6).
- Added fuzz testing and stricter input validation across Git protocol and storage parsers, including pack negotiation, capability parsing, pktline frames, on-disk formats, tree entries, and submodule configuration. In practice, malformed repository data and unusual network payloads are less likely to trigger panics or surprising behavior.
  - Thank you, [@hiddeco](https://github.com/hiddeco) for your contribution!

### Archive Support

- Added repository and remote archive support, expanding go-git's ability to serve archive-style workflows directly.
  - Thank you, [@aymanbagabas](https://github.com/aymanbagabas) for your contribution!

### Reliability Fixes

- Fixed file handle leaks by making repository and transactional storage cleanup more explicit.
  - Thank you, [@AriehSchneier](https://github.com/AriehSchneier), for your contribution!
- `Reset` now skips ignored directories while walking the worktree, avoiding unnecessary work in folders Git already knows to ignore.
- HashesWithPrefix now searches across alternates.
  - Thank you, [@andrew](https://github.com/andrew), for your contribution!

That's the dispatch. Pi is built in, Amp is connected, commits point more clearly to their sessions, [`git-sync`](https://github.com/entireio/git-sync) handles real-world mirrors, and [`go-git`](https://github.com/go-git/go-git) has fewer ways to be surprised by strange input.

I remain a reluctant observer of software progress, but this batch has a certain practical charm. And who doesn't love pie.

As always, drop into our [Discord](https://discord.com/invite/jZJs3Tue4S) with questions, feature requests, or philosophical musings about the nature of software. Or file an [issue](https://github.com/entireio/cli/issues) if you've found a bug.

Boop.
